import { gql } from '@apollo/client';
import { WarningRounded } from '@mui/icons-material';
import { Avatar } from '@mui/material';
import React from 'react';

import { formatInitials } from '../../utils/formatting';
import { ReturnComponent } from '../../utils/reactTypes';
import { NONE_TEXT } from '../displayFields/AxleInfoField';
import { AxleChip } from '../displayFields/chips';
import { ClinicianInactiveTag } from './ClinicianInactiveTag';

// Keeps the avatar readable while dropping it out of the row's foreground.
const INACTIVE_OPACITY = 0.6;

export const ClinicianChipFragment = gql`
    fragment ClinicianChipFragment on Clinician {
        id
        firstName
        lastName
        logoUrl
        isActive
    }
`;

export interface IClinicianChip {
    id: string;
    firstName: string;
    lastName: string;
    logoUrl: string | null;
    // Sources that don't select it render as they always have.
    isActive?: boolean;
}

export interface IClinicianChipProps {
    clinician: IClinicianChip | null;
    showLink: boolean;
    wrap?: boolean;
    services?: {
        id: string;
        name: string;
    }[];
    showServices?: boolean;
    color?: string;
    title?: string;
    onMouseEnter?: (event: React.MouseEvent<HTMLElement>) => void;
    onMouseLeave?: (event: React.MouseEvent<HTMLElement>) => void;
}

// Memoized because this is rendered once per clinician in dense views like the
// schedule calendar's column headers (hundreds of instances). Those parents
// re-render frequently (e.g. react-big-calendar re-renders its whole grid on
// any event click), and without memoization every chip — plus its Avatar,
// Tooltip and Popper subtree — re-renders even when the clinician hasn't
// changed. The component is pure, so a shallow prop compare is safe.
export const ClinicianChip = React.memo(function ClinicianChip({
    clinician,
    showLink,
    wrap = false,
    services = [],
    showServices = false,
    color,
    title,
    onMouseEnter,
    onMouseLeave,
}: IClinicianChipProps): ReturnComponent {
    const serviceLabel =
        showServices && services.length > 0
            ? ' (' + [...new Set(services.map((service) => service.name))].join(', ') + ')'
            : '';
    if (!clinician) {
        return <AxleChip icon={<WarningRounded />} label={NONE_TEXT + serviceLabel} />;
    }
    const isInactive = clinician.isActive === false;
    const name = `${clinician.firstName} ${clinician.lastName}`.trim();
    const label = `${name} ${serviceLabel}${title ? ` - ${title}` : ''}`;
    return (
        <AxleChip
            avatar={
                <Avatar
                    src={clinician.logoUrl ?? undefined}
                    alt={name}
                    sx={isInactive ? { opacity: INACTIVE_OPACITY } : undefined}
                >
                    {formatInitials(clinician.firstName, clinician.lastName)}
                </Avatar>
            }
            label={
                isInactive ? (
                    <>
                        {label}
                        <ClinicianInactiveTag />
                    </>
                ) : (
                    label
                )
            }
            tooltip={isInactive ? `${name} is inactive` : undefined}
            routerLink={showLink ? `/clinicians/${clinician.id}` : undefined}
            sx={{
                backgroundColor: color ? 'FFFFFF' : undefined,
                color: color ?? (isInactive ? 'text.disabled' : undefined),
                border: color ? `2px solid ${color}` : undefined,
                fontWeight: color ? '500' : undefined,
                ...(wrap && {
                    // If the label wraps across multiple lines, make sure all lines get fitted.
                    height: '100%',
                    // We need this, otherwise if the label is all on one line, the chip is too thin,
                    //  and looks weird in comparison to other chips.
                    minHeight: '32px',
                    '& .MuiChip-label': {
                        whiteSpace: 'normal',
                        textAlign: 'left',
                        lineHeight: '1.25em',
                    },
                }),
            }}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        />
    );
});
